import { useState, useEffect, useMemo } from "react";
import { useTable } from "react-table";
import { useFormContext } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

import Button from "components/atoms/Button/Button";
import Dialog from "components/organisms/Dialog/Dialog";
import DataTable from "components/organisms/DataTable/DataTable";
import { QuotationFreightModeOption } from "types/Common";
import testIds from "constants/testIds";
import getLatestQuotationColumns from "./getLatestQuotationColumns";
import { useGetLatestQuotation } from "./useGetLatestQuotation";

interface IReuseFormTemplate {
  isOpen: boolean;
  close: () => void;
  setShowQuotationConfirmModal: Function;
  freightModeOptions: QuotationFreightModeOption[];
  setReferenceId: Function;
}

function ReuseFormTemplate(props: IReuseFormTemplate) {
  const { isOpen, close, setShowQuotationConfirmModal, freightModeOptions, setReferenceId } = props;

  const [data, setData] = useState([]);

  const intl = useIntl();
  const { watch } = useFormContext();

  const selectedFreight = watch("service.freight_mode");
  const selectedFreightMode: QuotationFreightModeOption[] = freightModeOptions.filter(
    (freight) => freight.value === selectedFreight,
  );
  const freightServiceIds = freightModeOptions.map((item) => item.service_level_id).join(",");

  // Get latest quotations on the basis of params
  const {
    data: latestQuotationsData,
    refetch,
    isFetching,
  }: any = useGetLatestQuotation(
    {
      freight_mode: selectedFreight ? selectedFreightMode[0]?.service_level_id : freightServiceIds,
      results: 5,
    },
    { enabled: selectedFreight ? !!selectedFreight : !!freightServiceIds, refetchOnMount: false },
  );

  const fallbackMessage = "No quotation found.";
  const columns = useMemo(() => getLatestQuotationColumns(intl), []);

  const instance = useTable<any>({
    columns,
    data,
  });

  useEffect(() => {
    if (latestQuotationsData?.length) {
      setData(latestQuotationsData);
    } else {
      setData([]);
    }
  }, [latestQuotationsData]);

  // Refetch latest quotation api if there is any change in selected freight mode
  useEffect(() => {
    refetch();
  }, [selectedFreight, refetch]);

  const handleRowClick = (event: any) => {
    setShowQuotationConfirmModal(true);
    setReferenceId(event.original.reference);
    close();
  };

  return (
    <Dialog
      size="sm"
      title={intl.formatMessage({
        id: "quote.latestQuotations",
        defaultMessage: "Latest Quotations",
      })}
      show={isOpen}
      onHide={close}
    >
      <FormattedMessage
        id="general.latest-quotations-fuideline"
        defaultMessage="Please select from the most recent quotations to reuse."
      />
      <div style={{ width: "700px", maxHeight: "371px", overflow: "auto" }}>
        <DataTable
          customNoResults={fallbackMessage}
          tableInstance={instance}
          isLoading={isFetching}
          onRowClick={handleRowClick}
        />
      </div>
      <div className="d-flex justify-content-between mt-5">
        <Button
          variant="outline-primary"
          onClick={close}
          label={intl.formatMessage({
            id: "settings.provider.close",
            defaultMessage: "Close",
          })}
          data-testid={testIds.QUOTATION_FORM_TEMPLATE_CLOSE_BUTTON}
        />
        <Button
          variant="primary"
          label={intl.formatMessage({
            id: "quotation-form.template.reuseQuotation",
            defaultMessage: "Reuse quotation",
          })}
          type="submit"
          data-testid={testIds.QUOTATION_FORM_TEMPLATE_REUSE_QUOTATION_BUTTON}
          disabled
        />
      </div>
    </Dialog>
  );
}

export default ReuseFormTemplate;
